import React from 'react';
import { Field, Form } from 'formik';

// Material UI
import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';

// Data
import messages from './create-assessment.messages';
import { IAssessmentInfoForm, ICreateAssessmentFields } from './create-assessment.interface';

// Components
import { FormikSettingCheckbox } from '../../_core/Formik/formik-setting-checkbox.component';
import { RetainJerseyAssignmentsField } from '../../JerseyAssignment/retain-jersey-assignments-field.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormHelperText } from '../../_core/_ui/forms.component';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { ASSESSMENT_DESCRIPTOR_OPTIONS, ASSESSMENT_INFO_FORM_ID, SEASON_OPTIONS } from './create-assessment.constants';
import { Bold } from '../../_core/_ui/typography.component';
import { FormikInput } from '../../_core/Formik/formik-input.component';
import { getMessages as getAttendanceMessages } from '../../Attendance/attendance.messages';
import { getMessages as getReportCardAccessMessages } from '../../ReportCardAccess/report-card-access.messages';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';

export const AssessmentInfoForm = <T extends Partial<ICreateAssessmentFields>>({ form, submitError, company, showSaved, update, canEditAttendanceDefault = false, canEditReportCardDefault = false }: IAssessmentInfoForm<T>) => {
  const { isSubmitting, values } = form;
  const termLookup = useCompanySportTerminology();
  const attendanceMessages = getAttendanceMessages(termLookup);
  const reportCardAccessMessages = getReportCardAccessMessages(termLookup);

  return (
    <Form id={ASSESSMENT_INFO_FORM_ID}>
      <Bold style={{fontWeight: "bold"}}>Assessment Name (used in many emails)</Bold>
      <Box display="flex" flexDirection="row" justifyContent="spaceBetween" alignItems="end" style={{gap: "1rem"}}>
        <Field
          fid="year"
          name="year"
          component={FormikInput}
          label={messages.lblYear}
          type="number"
          min={new Date().getFullYear() - 1}
          max={new Date().getFullYear() + 2}
          step={1}
          margin="dense"
          />
        <Field fid="season" name="season" component={FormikSelect} label={messages.lblSeason} options={SEASON_OPTIONS} placeholder="Select season..." />
        <Field fid="descriptor" name="descriptor" component={FormikSelect} label={messages.lblDescriptor} options={ASSESSMENT_DESCRIPTOR_OPTIONS} placeholder="Select descriptor..." canCreate />
        <Field fid="additionalContext" name="additionalContext" component={FormikInput} label={messages.lblAdditionalContext} placeholder="Enter any additional context..." margin="dense"  />
      </Box>
      {!!company &&
        <Box mt={2} mb={2}>
          <Typography variant="body2" style={{fontWeight: "bold"}}>Example of Assessment Name in a Welcome Email</Typography>
          <Typography variant="body2">
            "Welcome to the {company.name} <u>{`${values.year} ${values.season?.value} ${values.descriptor?.value} ${values.additionalContext}`.trim()}</u>"
          </Typography>
        </Box>
      }

      <Field fid="csf" name="infoLink" component={FormikInput} label={messages.lblInfoLink} />

      <Field fid="ucf" name="evaluationGroupName" label={messages.lblCompanyEvaluationGroupName} component={FormikInput} />
      <Field fid="ucf" name="introLink" label={messages.lblCompanyIntroLink} component={FormikInput} />

      {update && canEditAttendanceDefault &&
        <Box mt={2} mb={1}>
          <FormikSettingCheckbox
            fid="csf"
            name="isAttendanceRequestedDefault"
            triState
            label={attendanceMessages.lblRequestRsvpsDefault}
            caption={<IntlMsg {...attendanceMessages.captionRequestRsvpsAssessmentDefault} />}
          />
          <RetainJerseyAssignmentsField scope="assessment" fid="csf" />
        </Box>
      }

      {update && canEditReportCardDefault &&
        <Box mt={2} mb={1}>
          <FormikSettingCheckbox
            fid="csf"
            name="reportCardsIncludeNonConcludedDefault"
            triState
            label={reportCardAccessMessages.lblIncludeNonConcludedDefault}
            caption={<IntlMsg {...reportCardAccessMessages.captionIncludeNonConcludedAssessmentDefault} />}
          />
        </Box>
      }

      {/* <Box width="188px" mt={1}>
        <InputLabel>Conclude Email Template</InputLabel>
        <Select
          value={options[0]}
          placeholder="Select conclude email template..."
          options={options}
          // onChange={onChangeColumnMapping}
        />
      </Box> */}

      <FormControl margin="dense">
        <Box display="flex" flexDirection="row">
          <PrimaryButton disabled={isSubmitting} type="submit">
            {isSubmitting ? <IntlMsg {...messages.btnSubmitting} /> : <IntlMsg {...(update ? messages.btnUpdate : messages.btnCreate)} />}
          </PrimaryButton>
          {submitError && <Box ml={2}><FormHelperText error>{submitError}</FormHelperText></Box>}
          {showSaved && <Box ml={2}><FormHelperText className="success">Assessment Info Saved</FormHelperText></Box>}
        </Box>
      </FormControl>

      <FocusFirstInvalidField formId={ASSESSMENT_INFO_FORM_ID} />
    </Form>
  );
};
